import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight } from "lucide-react";
import heroFaceDetection from "@/assets/hero-face-detection.jpg";
import heroBibDetection from "@/assets/hero-bib-detection.jpg";
import heroNumberDetection from "@/assets/hero-number-detection.jpg";

const heroImages = [
  { src: heroFaceDetection, alt: "AI facial recognition detecting runners at a marathon" },
  { src: heroBibDetection, alt: "AI bib number detection on cyclists during a race" },
  { src: heroNumberDetection, alt: "AI face detection on swimmers in a triathlon" },
];

const HeroSection = () => {
  const [currentImage, setCurrentImage] = useState(0);
  const { t } = useTranslation();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentImage((prev) => (prev + 1) % heroImages.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  return (
    <section className="relative min-h-screen flex items-center overflow-hidden">
      <div className="absolute inset-0">
        {heroImages.map((image, index) => (
          <img key={image.alt} src={image.src} alt={image.alt} className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-1000 ${index === currentImage ? "opacity-100" : "opacity-0"}`} />
        ))}
        <div className="absolute inset-0 bg-gradient-to-r from-hawk-navy/95 via-hawk-navy/80 to-hawk-navy/60" />
        <div className="absolute inset-0 bg-radial-orange opacity-50" />
      </div>
      <div className="container mx-auto px-4 pt-32 pb-20 relative z-10">
        <div className="max-w-3xl">
          <div className="text-white space-y-8">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 animate-fade-in">
              <span className="w-2 h-2 rounded-full bg-hawk-orange animate-pulse" />
              <span className="text-sm font-medium">{t("hero.badge")}</span>
            </div>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold leading-tight animate-fade-in" style={{ animationDelay: "0.1s" }}>
              {t("hero.titleFor")} <span className="text-gradient-primary">{t("hero.sharing")}</span> {t("hero.and")} <span className="text-gradient-primary">{t("hero.selling")}</span> {t("hero.photos")}
            </h1>
            <p className="text-lg md:text-xl text-white/80 max-w-xl animate-fade-in" style={{ animationDelay: "0.2s" }}>{t("hero.subtitle")}</p>
            <div className="flex flex-wrap gap-4 animate-fade-in" style={{ animationDelay: "0.3s" }}>
              {/* Real links (crawlable), with descriptive text rather than "Get Started". */}
              <Button variant="hero" size="lg" className="group" asChild>
                <Link to="/register">
                  {t("nav.signUpFree")}
                  <ArrowRight className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
              <Button variant="heroOutline" size="lg" className="group" asChild>
                <Link to="/find-your-photos">
                  {t("findPhotos.title")}
                  <ArrowRight className="group-hover:translate-x-1 transition-transform" />
                </Link>
              </Button>
            </div>
            <div className="grid grid-cols-4 gap-6 pt-8 border-t border-white/20 animate-fade-in" style={{ animationDelay: "0.4s" }}>
              {[
                { value: "75M+", label: t("hero.photosUploaded") },
                { value: "185M+", label: t("hero.peopleDetected") },
                { value: "450+", label: t("hero.members") },
                { value: "29", label: t("hero.countries") },
              ].map((stat) => (
                <div key={stat.label}>
                  <div className="text-3xl md:text-4xl font-display font-bold text-hawk-orange">{stat.value}</div>
                  <div className="text-sm text-white/60">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
